// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Run in terminal from a reply's shell block (AgentCodeRun): opens the chat's Terminals panel,
// connects, takes an idle terminal opened from Konvo (else opens one in the chat's folder) and types
// the commands into it, with Return unless one of them is risky (utils/AgentShellCommands.ts).

import AgentSidePanel from '@app/features/konvo_agents/state/AgentSidePanel';
import AgentTerminalSessions from '@app/features/konvo_agents/state/AgentTerminalSessions';
import {terminalInput} from '@app/features/konvo_agents/utils/AgentShellCommands';
import type {LinkProblem} from '@app/features/konvo_agents/utils/AgentTerminalLink';
import {when} from 'mobx';

export interface RunTarget {
	machineId: string;
	threadId: string;
	phone: boolean;
	/** Opens the chat's Terminals panel (default). Off: the caller shows the terminal itself (sign-in). */
	panel?: boolean;
}

export interface RunStarted {
	/** The terminal it went to. */
	id: string;
	/** Typed in, waiting for the person's Return (a risky command). */
	waits: boolean;
}

/** A terminal just given commands counts as busy this long, before its own status says so. */
const JUST_USED_MS = 4_000;
const CONNECT_TIMEOUT_MS = 20_000;

const justUsed = new Map<string, number>();
const usedKey = (target: RunTarget, id: string) => `${target.machineId}\u0000${target.threadId}\u0000${id}`;

/** An idle terminal opened from Konvo: the one picked in the panel first. */
function idleTerminal(target: RunTarget): string | null {
	const list = AgentTerminalSessions.list(target.machineId, target.threadId) ?? [];
	// The list says busy (`running`) or `idle`; a terminal's own state only says whether it lives.
	const idle = list.filter((t) => {
		if (!t.own || t.status !== 'idle') return false;
		const state = AgentTerminalSessions.stateOf(target.machineId, target.threadId, t.id)?.status;
		if (state && !['running', 'idle', 'starting'].includes(state)) return false;
		return (justUsed.get(usedKey(target, t.id)) ?? 0) < Date.now();
	});
	const picked = AgentTerminalSessions.pickedId(target.machineId, target.threadId);
	return (idle.find((t) => t.id === picked) ?? idle[0])?.id ?? null;
}

/** Runs `text` (one command or several, one per line) in a terminal of the chat. */
export async function runInTerminal(target: RunTarget, text: string): Promise<RunStarted> {
	const {machineId, threadId} = target;
	if (target.panel !== false) AgentSidePanel.open(`${machineId}/${threadId}`, {kind: 'terminals'});
	// Hold the connection while this runs; the panel takes its own once it shows.
	const release = AgentTerminalSessions.use(machineId, threadId);
	try {
		await when(
			() =>
				AgentTerminalSessions.link(machineId)?.status === 'failed' ||
				(AgentTerminalSessions.link(machineId)?.status === 'ready' &&
					AgentTerminalSessions.list(machineId, threadId) !== null),
			{timeout: CONNECT_TIMEOUT_MS},
		).catch(() => {
			throw {code: 'TIMEOUT', message: 'The machine did not answer.'} satisfies LinkProblem;
		});
		const link = AgentTerminalSessions.link(machineId);
		if (link?.status === 'failed') throw link.problem ?? {code: 'FAILED', message: 'Could not connect.'};
		let id = idleTerminal(target);
		if (!id) {
			const size = target.phone ? {cols: 46, rows: 24} : {cols: 90, rows: 28};
			id = await AgentTerminalSessions.openTerminal(machineId, threadId, size.cols, size.rows);
			if (!id)
				throw (
					AgentTerminalSessions.notice.get(machineId) ?? {code: 'NOT_OPENED', message: 'No terminal could be opened.'}
				);
		}
		AgentTerminalSessions.pick(machineId, threadId, id);
		justUsed.set(usedKey(target, id), Date.now() + JUST_USED_MS);
		const {data, waits} = terminalInput(text);
		AgentTerminalSessions.input(machineId, threadId, id, data);
		return {id, waits};
	} finally {
		release();
	}
}
